import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { useOrder } from '../OrderContext';
import { RushLogo } from '../components';
import LanguageToggle from '../components/LanguageToggle';

const GENDERS = [
  { value: 'male',    label: () => t.formGenderMale    },
  { value: 'female',  label: () => t.formGenderFemale  },
  { value: 'other',   label: () => t.formGenderOther   },
] as const;

const UserFormPage = () => {
  const navigate = useNavigate();
  const { updateOrder } = useOrder();

  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<string>('');
  const [errors, setErrors] = useState<{ name?: string; age?: string; gender?: string }>({});

  const validate = () => {
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Please enter your name';
    const ageNum = parseInt(age, 10);
    if (!age || isNaN(ageNum) || ageNum < 1 || ageNum > 120) next.age = 'Please enter a valid age';
    if (!gender) next.gender = 'Please select a gender';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    updateOrder({ user: { name: name.trim(), age: parseInt(age, 10), gender } });
    navigate('/menu-type');
  };

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col items-center overflow-y-auto px-4 py-6 sm:px-5 [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />

      {/* Language switcher — top-right of the very first screen */}
      <div className="absolute right-3 top-3 z-20 sm:right-5 sm:top-5">
        <LanguageToggle />
      </div>

      <div className="relative z-10 my-auto w-full max-w-md">
        {/* Logo */}
        <div className="mb-6 flex justify-center sm:mb-8">
          <RushLogo className="h-10 w-auto opacity-90 sm:h-12" />
        </div>

        {/* Card */}
        <div className="rounded-[24px] border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--card-shadow)] sm:p-7">
          <h1 className="mb-1 font-display text-[clamp(1.6rem,5vw,2.2rem)] font-bold text-[var(--cream)]">
            {t.formTitle}
          </h1>
          <p className="mb-6 text-[0.9rem] text-[var(--muted)]">Tell us a bit about yourself</p>

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
            {/* Name */}
            <div className="flex flex-col gap-1.5">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.formNamePlaceholder}
                className="w-full rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none focus:border-[var(--gold-lt)] transition-colors"
                autoComplete="given-name"
              />
              {/* Always-present, fixed-height slot so showing an error fills
                  reserved space instead of growing the card. */}
              <span className="block min-h-[1.1rem] text-[0.8rem] leading-tight text-red-400">{errors.name}</span>
            </div>

            {/* Age */}
            <div className="flex flex-col gap-1.5">
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder={t.formAgePlaceholder}
                min={1}
                max={120}
                className="w-full rounded-[14px] border border-[var(--border)] bg-[rgba(255,255,255,0.06)] px-4 py-3.5 text-[1rem] text-white placeholder:text-[var(--muted)] outline-none focus:border-[var(--gold-lt)] transition-colors"
              />
              <span className="block min-h-[1.1rem] text-[0.8rem] leading-tight text-red-400">{errors.age}</span>
            </div>

            {/* Gender */}
            <div className="flex flex-col gap-2">
              <span className="text-[0.85rem] uppercase tracking-[0.12em] text-[var(--muted)]">{t.formGenderLabel}</span>
              <div className="grid grid-cols-3 gap-2">
                {GENDERS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setGender(value)}
                    className={`rounded-[12px] border py-3 text-[0.88rem] font-semibold transition-all duration-150 ${
                      gender === value
                        ? 'border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)] text-white shadow-[0_0_18px_var(--accent-pressed-shadow)]'
                        : 'border-[var(--border)] bg-[rgba(255,255,255,0.04)] text-[var(--muted)]'
                    }`}
                  >
                    {label()}
                  </button>
                ))}
              </div>
              <span className="block min-h-[1.1rem] text-[0.8rem] leading-tight text-red-400">{errors.gender}</span>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="touch-target mt-2 w-full rounded-[16px] border-2 border-[var(--confirm-border)] bg-[var(--confirm-bg)] py-4 font-display text-[1.05rem] font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_18px_var(--confirm-shadow)] transition-opacity active:opacity-80"
            >
              {t.formSubmit}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormPage;
